<script setup lang="ts">
import { useField, useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import * as zod from "zod";
import { onMounted, ref } from "vue";
import router from "@/router";
import BaseLoading from "@/components/Common/BaseLoading.vue";
import { toast } from "vue-sonner";
import { useShipManagementCompanyStore } from "@/stores/master/ship-management-company";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { ShipOwnerCompanyPayload } from "@/types/interface/ship-owner-company";
import { mapOptionMultiselect } from "@/utils/helper";
import { useShipOwnerCompanyStore } from "@/stores/master/ship-owner-company";
import shipOwnerCompany from "@/queries/master/ship-owner-company";

const idShipOwner = ref<string>("");
const isEdit = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const shipManagementCompanyStore = useShipManagementCompanyStore();
const shipOwnerCompanyStore = useShipOwnerCompanyStore();
const detailShipOwner = ref<any>();
const listShipCompany = ref([]);

const validationSchema = toTypedSchema(
  zod.object({
    name: zod.string().nonempty({ message: "Name is required" }),
    ship_company: zod.preprocess(
      (val) => (val && typeof val === "object" ? val.value : val),
      zod.number()
    ),
  })
);

const { handleSubmit, errors } = useForm({
  validationSchema,
});

const { value: name } = useField<string>("name");
const { value: ship_company } = useField<string>("ship_company");

const onSubmit = handleSubmit(async (values) => {
  isLoading.value = true;
  const payloadShipOwner: ShipOwnerCompanyPayload = {
    name: name.value,
    ship_management_company_id: ship_company.value?.value,
  };

  const responseApi = ref<any>();
  if (isEdit.value == true) {
    responseApi.value = await shipOwnerCompanyStore.dispatchUpdate(
      idShipOwner.value,
      payloadShipOwner
    );
  } else {
    responseApi.value = await shipOwnerCompanyStore.dispatchCreate(
      payloadShipOwner
    );
  }
  const { success, message } = responseApi.value;

  if (success == true) {
    setTimeout(() => {
      isLoading.value = false;
      toast.success(
        isEdit.value
          ? "Ship Owner Company Successfully Updated"
          : "Ship Owner Company Successfully Added"
      );
      router.push("/master/ship-owner-company");
    }, 1000);
  } else {
    isLoading.value = false;
    toast.error(message);
  }
});

const loadDetailShipOwner = async () => {
  const { success, message } = await shipOwnerCompanyStore.dispatchDetail(
    idShipOwner.value
  );
  if (success) {
    detailShipOwner.value = await shipOwnerCompanyStore.owner.data;
    name.value = detailShipOwner.value.name;
    ship_company.value = mapOptionMultiselect(
      detailShipOwner.value.id_ship_management_company,
      "id",
      "name"
    );
  }
};

const loadListShipCompany = async () => {
  const { success } = await shipManagementCompanyStore.dispatchGetList(
    1,
    99,
    ""
  );
  listShipCompany.value = mapOptionMultiselect(
    shipManagementCompanyStore.companies.items,
    "id",
    "name"
  );
};

onMounted(() => {
  loadListShipCompany();
  if (router.currentRoute.value.params.id != undefined) {
    isEdit.value = true;
    idShipOwner.value = router.currentRoute.value.params.id.toString();
    loadDetailShipOwner();
  }
});
</script>
<template>
  <div class="row justify-content-center">
    <div class="col-lg-12">
      <div class="card bg-white border-0 rounded-3 mb-4">
        <div class="card-body p-4">
          <div
            class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 mb-lg-4"
          >
            <h3 class="mb-0">Form Create Ship Owner Company</h3>
            <a
              class="btn btn-danger fw-semibold text-white px-3"
              type="button"
              href="/master/ship-management-company"
            >
              <i class="ri-arrow-drop-left-line text-gray-light"></i>
              Back To List Ship Owner Company
            </a>
          </div>
          <BaseLoading text="Processing Data..." v-if="isLoading" />
          <form
            @submit="onSubmit"
            class="row g-3 needs-validation form"
            v-if="!isLoading"
          >
            <div class="row">
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="name" class="form-label label text-secondary"
                    >Name <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="name"
                      v-model="name"
                      placeholder="Name"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.name }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label
                    for="ship_company"
                    class="form-label label text-secondary"
                    >Ship Management Company
                    <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <vSelect
                      v-model="ship_company"
                      :options="listShipCompany"
                      placeholder="Choose Category Store"
                    ></vSelect>
                  </div>
                  <span class="text-danger">{{ errors.ship_company }}</span>
                </div>
              </div>
              <div class="col-12 mt-5">
                <button
                  class="btn btn-primary fw-semibold text-white py-2 px-3"
                  type="submit"
                >
                  Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
